import { useCallback, useEffect, useRef, useSyncExternalStore } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '@core/auth/useAuth'
import { useAppStore } from '@core/store'
import {
  clearPendingHandoff,
  HANDOFF_STORED_EVENT,
  peekPendingHandoff,
  type HandoffPayload,
} from '@core/firebimHandoff'
import { viewerApi } from './lib/viewerApi'
import { viewpointsStore } from './lib/viewpointsStore'
import type { LoadedBcf } from '@modules/bcf/store/bcfClientSlice'
import { resolveGuidTargets } from './lib/resolveGuidTargets'
import { rawIfc } from './lib/rawIfc'

// ---------------------------------------------------------------------------
// FireBimHandoffReceiver: componente sem UI (renderiza null), montado UMA vez
// no PopupsHost, ao lado do DeepLinkLoader. Consome o handoff do VN2R-FireBIM.
//
// A CAPTURA dos bytes vive em core/firebimHandoff.ts, instalada no arranque
// (antes do gate de auth) e persistida em IndexedDB, para nao se perder se a
// aba comecar na pagina de login. Este componente so faz a outra metade:
// quando o loader do viewer esta pronto, DRENA o IndexedDB e carrega o IFC,
// importa o BCF e foca o elemento indicado pelo GlobalId.
//
// Drena em dois gatilhos: (a) quando o loader fica pronto (payload que ja
// estava em buffer, ex.: depois do login) e (b) quando chega um payload novo
// com o viewer ja montado (evento HANDOFF_STORED_EVENT). Consumo unico: o
// payload so e apagado depois de tentado o carregamento.
// ---------------------------------------------------------------------------

const wait = (ms: number) => new Promise((r) => setTimeout(r, ms))

/** Resolve GlobalIds (GUIDs IFC) para os localIds dos modelos carregados,
 *  torna-os visiveis, selecciona-os todos e enquadra a camara neles. Um
 *  compartimento (IfcZone) nao tem geometria: o FireBIM ja costuma enviar os
 *  espacos membros em focusIds, mas se vier o GUID da propria zona o
 *  resolveGuidTargets expande-a para os membros (mesma resolucao que os
 *  viewpoints BCF usam). Os IfcSpace vem escondidos por defeito, dai o
 *  setItemsVisibility. Best-effort com algumas tentativas: logo apos o load o
 *  indice guid->localId pode ainda nao estar pronto. Modelos onde o GUID nao
 *  existe sao saltados. Devolve quantos elementos foram encontrados. */
async function focusByGlobalIds(globalIds: string[]): Promise<number> {
  if (globalIds.length === 0) return 0
  let targets = new Map<string, Set<number>>()
  for (let attempt = 1; attempt <= 5; attempt++) {
    if (attempt > 1) await wait(600)
    targets = new Map()
    for (const [modelId] of viewerApi.iterModels()) {
      const ids = await resolveGuidTargets(modelId, globalIds)
      if (ids.length > 0) targets.set(modelId, new Set(ids))
    }
    if (targets.size > 0) break
  }

  let count = 0
  for (const s of targets.values()) count += s.size
  if (count === 0) return 0

  // Torna os alvos visiveis (os espacos de um compartimento vem escondidos
  // por defeito) e selecciona + enquadra todos de uma vez.
  const idMap: Record<string, Set<number>> = {}
  for (const [modelId, ids] of targets) {
    await viewerApi.setItemsVisibility(modelId, [...ids], true)
    idMap[modelId] = ids
  }
  const h = viewerApi.getHighlighter()
  if (h) {
    try {
      // (style, idMap, reset, zoom): reset limpa a seleccao anterior; zoom
      // enquadra a camara em todos os alvos.
      await h.highlightByID('select', idMap, true, true)
    } catch (err) {
      console.warn('[firebim-handoff] highlightByID failed', err)
    }
  }
  // Reflecte uma seleccao representativa no painel (a store guarda uma so).
  for (const [modelId, ids] of targets) {
    const first = [...ids][0]
    if (typeof first === 'number') {
      useAppStore.getState().setSelection({ modelId, localId: first })
      break
    }
  }
  return count
}

/** O topico do BCF cujo viewpoint importado seleciona o GlobalId do finding.
 *  Percorre os viewpoints persistidos no import; null quando nao ha
 *  correspondencia (o painel abre na lista, como antes). */
async function findTopicGuidByGlobalId(
  loaded: LoadedBcf,
  globalId: string | undefined,
): Promise<string | null> {
  if (!globalId) return null
  const parsed = loaded.versions[loaded.versions.length - 1]?.parsed
  for (const topic of parsed?.topics ?? []) {
    for (const vid of topic.viewpointRefs ?? []) {
      try {
        const v = await viewpointsStore.get(vid)
        if (v?.viewpoint?.components?.selected_global_ids?.includes(globalId)) {
          return topic.guid
        }
      } catch {
        /* best effort */
      }
    }
  }
  return null
}

async function processPayload(
  msg: HandoffPayload,
  openBcf: (localId: string, topicGuid: string | null) => boolean,
): Promise<void> {
  const { pushToast, bcfImportFile } = useAppStore.getState()
  // 1) IFC: substitui o modelo actual (o handoff traz um modelo novo).
  try {
    const file = new File([msg.ifc], msg.ifcName || 'modelo.ifc', {
      type: 'application/octet-stream',
    })
    // O carregador não rejeita: devolve { ok: false, erro }. Antes, uma
    // falha passava em silêncio e o toast dizia "carregado".
    const aberto = await viewerApi.loadIfcFile(file, { replace: true })
    if (!aberto.ok) throw new Error(aberto.erro)
  } catch (err) {
    const detail = err instanceof Error ? err.message : String(err)
    pushToast(`Falha a carregar o IFC do FireBIM: ${detail}`, {
      type: 'error',
      timeout: 0,
    })
    return
  }
  // Janela de assentamento: em arranque cedo o mundo 3D pode ser remontado
  // (StrictMode em dev, observado tambem em producao) e varrer o modelo.
  await wait(400)

  // 2) BCF (opcional): importa-o para a biblioteca de BCFs. Falha aqui e
  // nao-fatal: o modelo ja carregou.
  let loadedBcf: LoadedBcf | null = null
  if (msg.bcf) {
    try {
      const bcfFile = new File([msg.bcf], msg.bcfName || 'topicos.bcfzip', {
        type: 'application/zip',
      })
      loadedBcf = await bcfImportFile(bcfFile)
    } catch (err) {
      console.warn('[firebim-handoff] BCF import failed', err)
      pushToast('IFC carregado; não foi possível ler o BCF do FireBIM.', {
        type: 'info',
      })
    }
  }

  // Garante o parser rawIfc para o(s) modelo(s) do handoff: e o que permite ao
  // resolveGuidTargets resolver e MANTER na seleccao entidades sem geometria
  // (IfcZone/IfcSystem/IfcGroup) que os Fragments nao indexam. Sem isto, um
  // IfcZone enviado no focusIds seria descartado. Os bytes ja estao em cache
  // (useViewer.cacheBytes); best-effort e nao-fatal (os espacos membros que o
  // FireBIM tambem envia resolvem via Fragments mesmo que isto falhe).
  for (const [modelId] of viewerApi.iterModels()) {
    if (!rawIfc.hasModel(modelId)) {
      try {
        await rawIfc.loadModel(modelId)
      } catch (err) {
        console.warn('[firebim-handoff] rawIfc load failed', err)
      }
    }
  }

  // 3) Foco (opcional): para um compartimento (IfcZone) os focusIds trazem o
  // GUID da zona e os espacos membros; o resolveGuidTargets mantem a zona na
  // seleccao (inspector) e enquadra a camara nos membros com geometria.
  const focusIds =
    msg.focusIds && msg.focusIds.length > 0
      ? msg.focusIds
      : msg.globalId
        ? [msg.globalId]
        : []
  const framed = focusIds.length > 0 ? await focusByGlobalIds(focusIds) : 0

  // 4) Abrir o BCF importado: navega para a vista de topicos (painel flutuante
  // sobre o viewer), para o utilizador VER o BCF em vez de o descobrir na
  // biblioteca (Rui, 2026-07-17). So no modo cloud (a rota e por workspace);
  // no modo local o import para a biblioteca mantem-se como antes.
  let bcfOpened = false
  if (loadedBcf) {
    const topicGuid = await findTopicGuidByGlobalId(loadedBcf, msg.globalId)
    bcfOpened = openBcf(loadedBcf.localId, topicGuid)
  }

  if (framed > 0) {
    pushToast(
      bcfOpened
        ? 'Modelo do FireBIM carregado; BCF aberto; elemento focado.'
        : 'Modelo do FireBIM carregado; elemento focado.',
      { type: 'success' },
    )
  } else if (focusIds.length > 0) {
    pushToast(
      'Modelo do FireBIM carregado; o elemento indicado não foi encontrado.',
      { type: 'info' },
    )
  } else {
    pushToast(
      bcfOpened
        ? 'Modelo do FireBIM carregado; BCF aberto.'
        : 'Modelo do FireBIM carregado.',
      { type: 'success' },
    )
  }
}

function FireBimHandoffReceiver() {
  const loaderReady = useSyncExternalStore(
    viewerApi.subscribe,
    viewerApi.hasIfcLoader,
  )
  const navigate = useNavigate()
  const { workspace } = useAuth()
  const workspaceSlug = workspace?.slug ?? null
  // Abre a vista de topicos do BCF importado (painel flutuante sobre o viewer).
  // Devolve false no modo local (sem workspace nao ha rota /bcf), onde o
  // import para a biblioteca se mantem como unico efeito.
  const openBcf = useCallback(
    (localId: string, topicGuid: string | null): boolean => {
      if (!workspaceSlug) return false
      const suffix = topicGuid ? `?topic=${encodeURIComponent(topicGuid)}` : ''
      navigate(`/${workspaceSlug}/bcf/${localId}${suffix}`)
      return true
    },
    [navigate, workspaceSlug],
  )
  // Consumo unico por vida da aba; drainingRef trava drenagens concorrentes
  // (dois efeitos de StrictMode, ou loader-ready e evento em simultaneo).
  const processedRef = useRef(false)
  const drainingRef = useRef(false)

  // Le hasIfcLoader() diretamente (nao o valor de render, que pode estar
  // desatualizado dentro de um handler). So depende de refs/viewerApi, logo
  // a identidade e estavel ([] deps).
  const drain = useCallback(async () => {
    if (processedRef.current || drainingRef.current) return
    if (!viewerApi.hasIfcLoader()) return
    drainingRef.current = true
    try {
      const payload = await peekPendingHandoff()
      if (!payload) return // nada a fazer; permite drenagens futuras
      processedRef.current = true
      try {
        await processPayload(payload, openBcf)
      } finally {
        // Consumo unico: apaga mesmo que o carregamento falhe (o toast ja
        // avisou), para nao recarregar um payload partido no proximo reload.
        await clearPendingHandoff()
      }
    } catch (err) {
      console.error('[firebim-handoff] drain failed', err)
    } finally {
      drainingRef.current = false
    }
  }, [openBcf])

  // Gatilho (a): loader ficou pronto. Drena um payload que ja tenha chegado
  // (tipico: chegou no login, viewer monta depois).
  useEffect(() => {
    if (loaderReady) void drain()
  }, [loaderReady, drain])

  // Gatilho (b): chegou um payload novo com o viewer ja montado.
  useEffect(() => {
    const onStored = () => void drain()
    window.addEventListener(HANDOFF_STORED_EVENT, onStored)
    return () => window.removeEventListener(HANDOFF_STORED_EVENT, onStored)
  }, [drain])

  return null
}

export default FireBimHandoffReceiver
